import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Theme,
} from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';

import { DialogTitleWithClose } from '@/components/common/DialogTitleWithClose';
import { Spinner } from '@/components/common/Spinner';
import { TokenContext } from '@/Contexts/TokenContext';
import { UsersContext } from '@/Contexts/UsersContext';
import useAuth from '@/hooks/useAuth';
import useUser from '@/hooks/useUser';
import { PartnerDashboardDetailsProps } from '@/models/PartnerDashboard';
import { StartUserImpersonationProps } from '@/models/StartUserImpersonationProps';
import { UserProps } from '@/models/User';

export default function PartnerImpersonationModal({
  modalPartner,
  modalOpen,
  usersInPartnerGroup,
  setModalOpen,
  theme,
}: {
  modalPartner: PartnerDashboardDetailsProps;
  modalOpen: boolean;
  usersInPartnerGroup: UserProps[] | null;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  theme: Theme;
}) {
  const { b2cId } = useContext(TokenContext);
  const { startUserImpersonation } = useUser();
  const { logoutCurrentUser } = useAuth();

  const { usersAll, userCurrent } = useContext(UsersContext);
  const [selectedUser, setSelectedUser] = useState<UserProps | null>(null);
  const [postingUser, setPostingUser] = useState<boolean>(false);

  useEffect(() => {
    if (usersInPartnerGroup && modalPartner?.partnerGroup?.id) {
      const defaultSelectedUser = usersInPartnerGroup?.find(
        (user: UserProps) => user.contactId === modalPartner.primaryContactId
      );

      if (defaultSelectedUser) {
        setSelectedUser(defaultSelectedUser);
      }
    }
  }, [modalPartner.partnerGroup?.id, modalPartner.primaryContactId, usersAll, usersInPartnerGroup]);

  const handleUserChange = (event: SelectChangeEvent) => {
    const newSelectedUser = usersInPartnerGroup?.find(
      (partnerUser) => partnerUser.b2CId === event.target.value
    );
    if (newSelectedUser) setSelectedUser(newSelectedUser);
  };

  const handleUserImpersonation = async () => {
    if (!userCurrent?.b2CId || !selectedUser?.b2CId) return;

    setPostingUser(true);

    if (!modalPartner.id) {
      throw new Error('No partner ID found for impersonation');
    }

    try {
      await startUserImpersonation({
        impersonatorB2CId: userCurrent.b2CId,
        impersonateeB2CId: selectedUser.b2CId,
        partnerId: modalPartner.id,
      } satisfies StartUserImpersonationProps);

      void logoutCurrentUser('Impersonation');
    } finally {
      setPostingUser(false);
    }
  };

  const handleClose = () => setModalOpen(false);

  return (
    <Dialog open={modalOpen} onClose={handleClose}>
      <DialogTitleWithClose onClose={handleClose}>Impersonate User</DialogTitleWithClose>
      <DialogContent>
        {!usersAll || !b2cId || !usersInPartnerGroup ? (
          <Spinner type='circular' />
        ) : (
          <>
            <FormControl fullWidth>
              <InputLabel id='impersonate-user-label-id'>{`User to Impersonate for ${modalPartner?.partnerName}`}</InputLabel>
              <Select
                labelId='impersonate-user-label-id'
                label={`User to Impersonate for ${modalPartner?.partnerName}`}
                onChange={handleUserChange}
                value={selectedUser ? selectedUser.b2CId : ''}
              >
                {usersInPartnerGroup.map((user: UserProps) => (
                  <MenuItem key={user.b2CId} value={user.b2CId}>
                    {user.contactId == modalPartner.primaryContactId ? '(Primary User) ' : ''}
                    {user.contact?.firstName} {user.contact?.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {!usersAll || !b2cId || !usersInPartnerGroup ? null : (
          <>
            <Button variant='secondary' size='large' onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant='primary'
              size='large'
              onClick={() => {
                handleUserImpersonation();
              }}
              disabled={postingUser}
            >
              {postingUser ? (
                <>
                  <span>Updating ... </span>
                  <CircularProgress
                    style={{
                      color: theme.palette.grey[500],
                      marginLeft: '5px',
                    }}
                    size='16px'
                  />
                </>
              ) : (
                `Impersonate`
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
